import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { products, categories } from '../../mocks/products';
import { ProductCard } from './components/ProductCard';
import { SocialSidebar } from '../../components/feature/SocialSidebar';
import { BlueProtectInfo } from './components/BlueProtectInfo';
import { EvilEnergyInfo } from './components/EvilEnergyInfo';
import { QualityPromise } from './components/QualityPromise';
import { useSEO } from '@/hooks/useSEO';

const ShopPage = () => {
  const { t } = useTranslation();

  const sortOptions = [
    { value: 'featured', label: t('shop_sort_featured') },
    { value: 'price_asc', label: t('shop_sort_price_asc') },
    { value: 'price_desc', label: t('shop_sort_price_desc') },
    { value: 'rating', label: t('shop_sort_rating') },
  ];

  useSEO({
    title: 'Shop | ZpiritualZ Den Haag – Spirituele Beschermingsproducten Kopen',
    description: 'Koop spirituele beschermingsproducten bij ZpiritualZ in Den Haag. Blue Protect Serie (indigofera), Evil Energy Destroy Serie (hing/asafoetida) en de complete Destroy Kit.',
    canonical: 'https://www.zpiritualz.com/shop',
    keywords: 'spirituele producten kopen, blue protect kopen, evil energy destroy kopen, hing zeep, asafoetida spray, spirituele webshop Nederland',
    ogImage: 'https://public.readdy.ai/ai/img_res/023720b702f22d737d505000a9bb0259.jpg',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Store',
      name: 'ZpiritualZ Webshop',
      url: 'https://www.zpiritualz.com/shop',
      description: 'Spirituele beschermingsproducten gebaseerd op eeuwenoude tradities.',
      currenciesAccepted: 'EUR',
      paymentAccepted: 'Credit Card, iDEAL',
      priceRange: '€9.95 - €79.95',
    },
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('featured');
  const [priceMax, setPriceMax] = useState(200);
  const [showCancelled, setShowCancelled] = useState(false);
  const activeCat = searchParams.get('cat') || 'all';

  useEffect(() => {
    if (searchParams.get('status') === 'cancelled') {
      setShowCancelled(true);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('status');
      setSearchParams(newParams, { replace: true });
      const timer = setTimeout(() => setShowCancelled(false), 7000);
      return () => clearTimeout(timer);
    }
  }, []);

  const filtered = useMemo(() => {
    let result = [...products];
    if (activeCat !== 'all') result = result.filter(p => p.category === activeCat);
    if (search) result = result.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));
    result = result.filter(p => p.price <= priceMax);
    if (sort === 'price_asc') result.sort((a, b) => a.price - b.price);
    if (sort === 'price_desc') result.sort((a, b) => b.price - a.price);
    if (sort === 'rating') result.sort((a, b) => b.rating - a.rating);
    return result;
  }, [activeCat, search, sort, priceMax]);

  return (
    <div className="min-h-screen bg-[#050010] pt-20">
      <SocialSidebar />

      {/* Geannuleerde betaling toast */}
      <div className={`fixed top-24 left-1/2 -translate-x-1/2 z-50 transition-all duration-500 ${showCancelled ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-4 pointer-events-none'}`}>
        <div className="flex items-start gap-4 bg-[#1a0010] border border-rose-500/35 rounded-2xl px-6 py-4 shadow-[0_8px_40px_rgba(244,63,94,0.15)] max-w-md w-[90vw]">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-rose-500/15 border border-rose-500/25 shrink-0 mt-0.5">
            <i className="ri-close-circle-line text-rose-400 text-lg" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold text-sm mb-0.5">{t('shop_cancelled_title')}</p>
            <p className="text-white/45 text-xs leading-relaxed">{t('shop_cancelled_msg')}</p>
          </div>
          <button onClick={() => setShowCancelled(false)}
            className="w-7 h-7 flex items-center justify-center text-white/30 hover:text-white/70 transition-colors cursor-pointer shrink-0 mt-0.5">
            <i className="ri-close-line text-base" />
          </button>
        </div>
        {showCancelled && (
          <div className="mt-1.5 mx-2 h-0.5 bg-rose-500/15 rounded-full overflow-hidden">
            <div className="h-full bg-rose-500/50 rounded-full animate-shrink" />
          </div>
        )}
      </div>

      {/* Hero Banner */}
      <div className="relative h-52 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=spiritual%20products%20shop%20collection%20crystals%20blue%20protect%20products%20cosmic%20dark%20purple%20background%20banner%20webshop%20spiritual%20wellness%20products%20elegant%20display%20violet%20glow&width=1920&height=400&seq=shopbanner2&orientation=landscape"
            alt="Shop banner"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#050010]/80" />
        </div>
        <div className="relative z-10 text-center">
          <h1 className="text-4xl font-black text-white mb-2">
            {t('shop_hero_title')} <span className="text-transparent bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text">{t('shop_hero_subtitle')}</span>
          </h1>
          <p className="text-white/40 text-sm">{t('shop_hero_desc')}</p>
        </div>
      </div>

      <BlueProtectInfo />
      <EvilEnergyInfo />
      <QualityPromise />

      <div id="producten" className="max-w-[1440px] mx-auto px-6 lg:px-12 py-10">
        {/* Search & Sort Bar */}
        <div className="flex flex-col lg:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-white/25 text-sm" />
            <input type="text" placeholder={t('shop_search_placeholder')} value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-[#110020]/80 border border-violet-500/15 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:border-violet-500/40 transition-colors" />
          </div>
          <select value={sort} onChange={e => setSort(e.target.value)}
            className="px-4 py-3 bg-[#110020]/80 border border-violet-500/15 rounded-xl text-sm text-white focus:outline-none focus:border-violet-500/40 transition-colors cursor-pointer appearance-none min-w-[200px]">
            {sortOptions.map(o => (
              <option key={o.value} value={o.value} className="bg-[#0d0020]">{o.label}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="w-full lg:w-60 shrink-0">
            <div className="bg-[#110020]/60 border border-violet-500/15 rounded-2xl p-5 mb-4">
              <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
                <i className="ri-filter-line text-violet-400" />
                {t('shop_categories')}
              </h3>
              <ul className="space-y-1">
                {categories.map(cat => (
                  <li key={cat.id}>
                    <button onClick={() => setSearchParams(cat.id === 'all' ? {} : { cat: cat.id })}
                      className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all cursor-pointer whitespace-nowrap ${
                        activeCat === cat.id ? 'bg-violet-600/20 text-violet-300 font-medium border border-violet-500/30' : 'text-white/45 hover:text-white hover:bg-white/5'
                      }`}>
                      {cat.id === 'blue-protect' && <span className="inline-block w-2 h-2 rounded-full bg-violet-400 mr-2 align-middle" />}
                      {cat.id === 'evil-energy-destroy' && <span className="inline-block w-2 h-2 rounded-full bg-red-400 mr-2 align-middle" />}
                      {cat.id === 'evil-energy-destroy-kit' && <span className="inline-block w-2 h-2 rounded-full bg-orange-400 mr-2 align-middle" />}
                      {cat.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-[#110020]/60 border border-violet-500/15 rounded-2xl p-5">
              <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
                <i className="ri-money-euro-circle-line text-violet-400" />
                {t('shop_max_price')}: <span className="text-violet-400">&euro;{priceMax}</span>
              </h3>
              <input type="range" min={10} max={300} value={priceMax} onChange={e => setPriceMax(Number(e.target.value))}
                className="w-full accent-violet-500 cursor-pointer" />
              <div className="flex justify-between text-white/25 text-xs mt-1">
                <span>&euro;10</span>
                <span>&euro;300</span>
              </div>
            </div>
          </aside>

          {/* Product Grid */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-5">
              <p className="text-white/40 text-sm">
                <span className="text-white font-medium">{filtered.length}</span> {t('shop_products_found')}
              </p>
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-search-line text-5xl text-white/15 block mb-4" />
                <p className="text-white/30">{t('shop_no_products')}</p>
              </div>
            ) : activeCat === 'all' ? (
              <div className="space-y-12" data-product-shop>
                {filtered.filter(p => p.category === 'blue-protect').length > 0 && (
                  <div>
                    <div className="mb-5 p-4 bg-gradient-to-r from-violet-600/15 to-fuchsia-600/15 border border-violet-500/25 rounded-xl flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center shrink-0">
                        <i className="ri-shield-star-line text-violet-400 text-lg" />
                      </div>
                      <div>
                        <p className="text-white font-semibold text-sm">{t('shop_filter_blue')}</p>
                        <p className="text-white/40 text-xs">{t('shop_blue_desc')}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                      {filtered.filter(p => p.category === 'blue-protect').map(product => (
                        <ProductCard key={product.id} product={product} />
                      ))}
                    </div>
                  </div>
                )}
                {filtered.filter(p => p.category === 'evil-energy-destroy').length > 0 && (
                  <div>
                    <div className="mb-5 p-4 bg-gradient-to-r from-red-900/20 to-orange-900/15 border border-red-500/25 rounded-xl flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center shrink-0">
                        <i className="ri-shield-flash-line text-red-400 text-lg" />
                      </div>
                      <div>
                        <p className="text-white font-semibold text-sm">{t('shop_filter_evil')}</p>
                        <p className="text-white/40 text-xs">{t('shop_evil_desc')}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                      {filtered.filter(p => p.category === 'evil-energy-destroy').map(product => (
                        <ProductCard key={product.id} product={product} />
                      ))}
                    </div>
                  </div>
                )}
                {filtered.filter(p => p.category === 'evil-energy-destroy-kit').length > 0 && (
                  <div>
                    <div className="mb-5 p-4 bg-gradient-to-r from-orange-900/20 to-red-900/15 border border-orange-500/25 rounded-xl flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center shrink-0">
                        <i className="ri-fire-line text-orange-400 text-lg" />
                      </div>
                      <div>
                        <p className="text-white font-semibold text-sm">{t('shop_filter_kit')}</p>
                        <p className="text-white/40 text-xs">{t('shop_kit_desc')}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                      {filtered.filter(p => p.category === 'evil-energy-destroy-kit').map(product => (
                        <ProductCard key={product.id} product={product} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                {activeCat === 'evil-energy-destroy-kit' && (
                  <div className="mb-5 p-4 bg-gradient-to-r from-orange-900/20 to-red-900/15 border border-orange-500/25 rounded-xl flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center shrink-0">
                      <i className="ri-fire-line text-orange-400 text-lg" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">{t('shop_filter_kit')}</p>
                      <p className="text-white/40 text-xs">{t('shop_kit_desc')}</p>
                    </div>
                  </div>
                )}
                {activeCat === 'blue-protect' && (
                  <div className="mb-5 p-4 bg-gradient-to-r from-violet-600/15 to-fuchsia-600/15 border border-violet-500/25 rounded-xl flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center shrink-0">
                      <i className="ri-shield-star-line text-violet-400 text-lg" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">{t('shop_filter_blue')}</p>
                      <p className="text-white/40 text-xs">{t('shop_blue_desc')}</p>
                    </div>
                  </div>
                )}
                {activeCat === 'evil-energy-destroy' && (
                  <div className="mb-5 p-4 bg-gradient-to-r from-red-900/20 to-orange-900/15 border border-red-500/25 rounded-xl flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center shrink-0">
                      <i className="ri-shield-flash-line text-red-400 text-lg" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">{t('shop_filter_evil')}</p>
                      <p className="text-white/40 text-xs">{t('shop_evil_desc')}</p>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5" data-product-shop>
                  {filtered.map(product => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopPage;
